import { observer } from 'mobx-react-lite';
import { Spinner, SpinnerType } from '@fluentui/react/lib/Spinner';
import { PdfJsPreview, PdfJsPreviewPrintable } from 'owa-sxs-pdf';
import {
    ImageGalleryItem,
    lazySetPreviewError,
    LoadedInfo,
    MIN_PREVIEW_PANE_WIDTH,
    PreviewPaneErrorViewState,
    PreviewPaneLoadingViewState,
    PreviewPaneMode,
    SxSViewState,
} from 'owa-sxsdata';
import * as React from 'react';
import { SxSCustomData } from '../utils/combineCustomData';
import AudioPreview from './AudioPreview';
import IFramePreview from './IFramePreview';
import ImagePreview, { ImagePreviewArrowCallbacks, ImagePreviewHandle } from './ImagePreview';
import NativePdfPreview from './NativePdfPreview';
import TextPreview from './TextPreview';
import VideoPreview from './VideoPreview';
import WacDocumentPreview from './WacDocumentPreview';
import { PreviewError } from './PreviewError';
import { FilterableMenuItem } from 'owa-filterable-menu';

export interface PreviewPaneProps {
    className: string;
    commandBar: any;
    errorCommandItems: FilterableMenuItem[];
    getExtraCustomDataForDatapoint: () => SxSCustomData;
    imagePreviewArrowCallbacks: ImagePreviewArrowCallbacks;
    onBeforeImageGalleryItemSelected: (imageGalleryItem: ImageGalleryItem) => Promise<void>;
    onError: (errorMessage: string, error?: Error) => void;
    onImageGalleryItemSelected: () => void;
    onLoad: (loadedInfo?: LoadedInfo) => void;
    renderCustomView: () => JSX.Element;
    style: React.CSSProperties;
    viewState: SxSViewState;
    onFocusCommandBar: () => void;
    onFocusRightHandPane: () => Promise<void>;
}

const styles = require('./PreviewPane.scss');
const classNames = require('classnames/bind').bind(styles);

export interface PreviewPaneHandle {
    onResize(): void;
    getFullScreenElement(): HTMLElement;
    pdfJsPrint(): void;
}

export default observer(
    function PreviewPane(props: PreviewPaneProps, ref: React.Ref<PreviewPaneHandle>) {
        React.useImperativeHandle(
            ref,
            () => ({
                onResize() {
                    if (imagePreview.current) {
                        imagePreview.current.onResize();
                    }
                },
                getFullScreenElement() {
                    return imagePreview.current && imagePreview.current.getFullScreenDiv();
                },
                pdfJsPrint() {
                    if (pdfJsPreview.current) {
                        pdfJsPreview.current.print();
                    }
                },
            }),
            []
        );
        React.useEffect(() => {
            if (imagePreview.current) {
                imagePreview.current.focus();
            }
        });
        const imagePreview = React.useRef<ImagePreviewHandle>();
        const pdfJsPreview = React.useRef<PdfJsPreviewPrintable>();
        const renderPreviewContent = () => {
            const className = styles.contentPreview;
            switch (props.viewState.previewPane.mode) {
                case PreviewPaneMode.Loading:
                    return renderActivityIndicator(props.viewState.previewPane);
                case PreviewPaneMode.Error:
                    return renderPreviewError(props.viewState.previewPane);
                case PreviewPaneMode.Image:
                    return (
                        <ImagePreview
                            arrowCallbacks={props.imagePreviewArrowCallbacks}
                            className={className}
                            getExtraCustomDataForDatapoint={props.getExtraCustomDataForDatapoint}
                            onBeforeImageGalleryItemSelected={
                                props.onBeforeImageGalleryItemSelected
                            }
                            onError={onError}
                            onImageGalleryItemSelected={props.onImageGalleryItemSelected}
                            onLoad={onLoad}
                            ref={imagePreview}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.Wac:
                    return (
                        <WacDocumentPreview
                            className={className}
                            onLoad={onLoad}
                            onError={onError}
                            viewState={props.viewState}
                            getExtraCustomDataForDatapoint={props.getExtraCustomDataForDatapoint}
                            onFocusCommandBar={props.onFocusCommandBar}
                            onFocusRightHandPane={props.onFocusRightHandPane}
                        />
                    );
                case PreviewPaneMode.NativePdf:
                    return (
                        <NativePdfPreview
                            className={className}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.Audio:
                    return (
                        <AudioPreview
                            className={className}
                            onError={onError}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.Video:
                    return (
                        <VideoPreview
                            className={className}
                            onError={onError}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.Text:
                    return (
                        <TextPreview
                            className={className}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.IFrame:
                    return (
                        <IFramePreview
                            className={className}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                        />
                    );
                case PreviewPaneMode.PdfJs:
                    return (
                        <PdfJsPreview
                            className={className}
                            onError={onError}
                            onLoad={onLoad}
                            viewState={props.viewState.previewPane}
                            ref={pdfJsPreview}
                        />
                    );
                case PreviewPaneMode.Custom:
                    return <div className={className}>{props.renderCustomView?.()}</div>;
                case PreviewPaneMode.Blank:
                default:
                    return <div className={className} />;
            }
        };
        const renderPreviewError = (previewPane: PreviewPaneErrorViewState) => {
            return <PreviewError viewState={previewPane} commandItems={props.errorCommandItems} />;
        };
        const onError = async (errorMessage: string, error?: Error) => {
            const setPreviewError = await lazySetPreviewError.import();
            setPreviewError(props.viewState, errorMessage);
            if (props.onError) {
                props.onError(errorMessage, error);
            }
        };
        const onLoad = (loadedInfo?: LoadedInfo) => {
            if (props.onLoad) {
                props.onLoad(loadedInfo);
            }
        };
        const minPreviewPaneWidth = props.viewState.minLeftPaneWidth || MIN_PREVIEW_PANE_WIDTH;
        return (
            <div
                className={classNames(props.className, styles.previewPane)}
                data-min-width={minPreviewPaneWidth}
                style={props.style}>
                {props.commandBar}
                {renderPreviewContent()}
            </div>
        );
    },
    { forwardRef: true }
);

function renderActivityIndicator(previewPane: PreviewPaneLoadingViewState) {
    return (
        <div className={styles.messageContainer}>
            <Spinner type={SpinnerType.large} />
            <div className={styles.loadingMessageText}>{previewPane.message}</div>
        </div>
    );
}
